import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { Calendar } from '../components/ui/calendar';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AuthContext } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_STAGES = ['Service', 'Date', 'Time', 'Details', 'Payment'];
const FIRST_STAGE_INDEX = 0;
const LAST_STAGE_INDEX = BOOKING_STAGES.length - 1;
const MIN_BOOKING_DAYS_AHEAD = 2;

const TIME_SLOTS = [
  '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
];

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function Booking() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stage, setStage] = useState(0); // Start at 0 for service selection
  const [selectedService, setSelectedService] = useState('');
  const [servicePrice, setServicePrice] = useState(parseFloat(process.env.REACT_APP_DEFAULT_SERVICE_PRICE || '50.0'));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [details, setDetails] = useState({
    firstName: '',
    lastName: '',
    houseNumber: '',
    streetName: '',
    aptNumber: '',
    city: 'Bloomington',
    zipCode: '',
    phone: '',
    email: ''
  });
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const services = [
    { id: 'lawn_mowing', name: 'Lawn Mowing', description: 'Includes: Lawn Mowing, Edge Trimming, and Debris Removal', price: 30, note: 'Starting at' },
    { id: 'junk_removal_house', name: 'Junk Removal - Full House', description: 'Complete house junk removal', price: 350, note: 'Starting at' },
    { id: 'junk_removal_garage', name: 'Junk Removal - Garage/Shed/Storage', description: 'Garage, shed & storage units', price: 150, note: 'Starting at' },
    { id: 'junk_removal_sidewalk', name: 'Junk Removal - Sidewalk', description: 'Sidewalk junk removal', price: 25, note: 'Starting at' },
    { id: 'yard_cleanup', name: 'Yard Cleanup', description: 'Includes: Leaves & sticks removal & raking', price: 35, note: 'Starting at' },
    { id: 'snow_removal', name: 'Snow Removal', description: 'Professional snow removal service', price: 30, note: 'Starting at' },
    { id: 'scrap_metal', name: 'Free Scrap/Junk Metal Pickup', description: 'Free pickup of scrap metal', price: 0 }
  ];
  const isFirstStage = stage === FIRST_STAGE_INDEX;
  const minimumBookingDate = new Date();
  minimumBookingDate.setHours(0, 0, 0, 0);
  minimumBookingDate.setDate(minimumBookingDate.getDate() + MIN_BOOKING_DAYS_AHEAD);

  useEffect(() => {
    if (!user) return;

    const nameParts = (user.name || '').trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');

    setDetails((current) => ({
      ...current,
      firstName: current.firstName || firstName,
      lastName: current.lastName || lastName,
      phone: current.phone || user.phone || '',
      email: current.email || user.email || '',
      houseNumber: current.houseNumber || user.house_number || '',
      streetName: current.streetName || user.street_name || '',
      aptNumber: current.aptNumber || user.apt_number || '',
      city: current.city || user.city || 'Bloomington',
      zipCode: current.zipCode || user.zip_code || ''
    }));
  }, [user]);

  useEffect(() => {
    const fetchOccupiedSlots = async () => {
      if (!selectedDate) {
        setOccupiedSlots([]);
        return;
      }

      try {
        const selectedDateValue = format(selectedDate, 'yyyy-MM-dd');
        const { data } = await axios.get(`${API}/bookings/occupied-slots`, {
          params: { date: selectedDateValue }
        });
        const occupiedTimes = data?.occupied_times || [];
        setOccupiedSlots(occupiedTimes);

        if (selectedTime && occupiedTimes.includes(selectedTime)) {
          setSelectedTime('');
          toast.error('That time slot is now occupied. Please choose another time.');
        }
      } catch (error) {
        console.error('Failed to fetch occupied slots:', error);
        setOccupiedSlots([]);
      }
    };

    fetchOccupiedSlots();
  }, [selectedDate, selectedTime]);

  const serviceAddressForMap = `${details.houseNumber} ${details.streetName}${details.aptNumber ? ` ${details.aptNumber}` : ''}, ${details.city}, IL ${details.zipCode}`.trim();
  const mapEmbedSrc = `https://maps.google.com/maps?q=${encodeURIComponent(serviceAddressForMap)}&output=embed`;

  const handleBack = () => {
    if (isFirstStage) {
      window.location.href = 'https://fastlanelawn.com';
      return;
    }

    setStage((currentStage) => Math.max(FIRST_STAGE_INDEX, currentStage - 1));
  };

  const handleNext = () => {
    if (stage === 0 && !selectedService) {
      toast.error('Please select a service');
      return;
    }
    if (stage === 1 && !selectedDate) {
      toast.error('Please select a date');
      return;
    }
    if (stage === 1 && selectedDate) {
      const normalizedSelectedDate = new Date(selectedDate);
      normalizedSelectedDate.setHours(0, 0, 0, 0);
      if (normalizedSelectedDate < minimumBookingDate) {
        toast.error(`Please select a date at least ${MIN_BOOKING_DAYS_AHEAD} days in advance`);
        return;
      }
    }
    if (stage === 2 && !selectedTime) {
      toast.error('Please select a time');
      return;
    }
    if (stage === 3) {
      if (!details.firstName || !details.lastName || !details.houseNumber || !details.streetName || !details.city || !details.zipCode || !details.phone || !details.email) {
        toast.error('Please fill all required fields');
        return;
      }
      if (!addressConfirmed) {
        setShowMap(true);
        return;
      }
    }
    setStage((currentStage) => Math.min(LAST_STAGE_INDEX, currentStage + 1));
  };

  const handleAddressConfirmation = (confirmed) => {
    if (confirmed) {
      // Convert address to uppercase
      setDetails({
        ...details,
        firstName: details.firstName.toUpperCase(),
        lastName: details.lastName.toUpperCase(),
        houseNumber: details.houseNumber.toUpperCase(),
        streetName: details.streetName.toUpperCase(),
        aptNumber: details.aptNumber.toUpperCase(),
        city: details.city.toUpperCase(),
        zipCode: details.zipCode.toUpperCase()
      });
      setAddressConfirmed(true);
      setShowMap(false);
      setStage((currentStage) => Math.min(LAST_STAGE_INDEX, currentStage + 1));
    } else {
      setShowMap(false);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service.id);
    setServicePrice(service.price);
  };

  const handlePayment = async () => {
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        name: `${details.firstName} ${details.lastName}`,
        address: `${details.houseNumber} ${details.streetName}${details.aptNumber ? ' ' + details.aptNumber : ''}, ${details.city}, IL ${details.zipCode}`,
        phone: details.phone,
        email: details.email,
        payment_method: paymentMethod,
        amount: servicePrice,
        service: selectedService
      };

      const { data } = await axios.post(`${API}/bookings`, bookingData, { withCredentials: true });

      if (paymentMethod === 'cash') {
        toast.success('Booking created! Pay cash on service day.');
        setTimeout(() => navigate('/my-account'), 2000);
      }

      else if (paymentMethod === 'stripe') {
        try {
          const { data: sessionData } = await axios.post(
            `${API}/payments/stripe/create-session`, {
            booking_id: data.booking_id,
            payment_type: 'stripe'
          }
          );

          if (!sessionData?.url) {
            throw new Error("Missing Stripe checkout URL");
          }

          window.location.href = sessionData.url;

        } catch (err) {
          console.error("Stripe session error:", err);
          alert("Failed to start Stripe checkout. Please try again.");
        }
      }

      else if (paymentMethod === 'paypal') {
        const { data: paypalData } = await axios.post(`${API}/payments/paypal/create-order`, {
          booking_id: data.booking_id,
          payment_type: 'paypal'
        });
        window.location.href = paypalData.approval_url;
      }
    } catch (error) {
      toast.error('Failed to create booking');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            data-testid="back-button"
            onClick={handleBack}
            className="flex items-center gap-2 font-bold uppercase text-sm hover:text-[#71717A] transition"
          >
            <ArrowLeft size={20} weight="bold" />
            {isFirstStage ? 'Back to Home' : `Back to ${BOOKING_STAGES[stage - 1]}`}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Progress Indicator */}
        <div className="mb-12" data-testid="progress-indicator">

          {/* wrapper */}
          <div className="relative mx-auto max-w-3xl">

            {/* background line */}
            <div className="absolute top-5 left-0 w-full h-1 bg-[#E4E4E7]" />

            {/* active progress line */}
            <div
              className="absolute top-5 left-0 h-1 bg-[#CCFF00] transition-all duration-300"
              style={{
                width: `${(stage / (BOOKING_STAGES.length - 1)) * 100}%`
              }}
            />

            {/* steps */}
            <div className="relative flex justify-between">
              {BOOKING_STAGES.map((label, i) => (
                <div
                  key={i}
                  onClick={() => {
                    if (i <= stage) setStage(i);
                  }}
                  className={`flex flex-col items-center cursor-pointer ${i <= stage ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
                    }`}
                >
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stage 0: Service Selection */}
        {stage === 0 && (
          <div data-testid="stage-0">
            <div className="mb-6">
              <button
                onClick={() => navigate('/quote')}
                className="w-full py-3 px-4 bg-white text-black border-2 border-black font-bold uppercase text-sm hover:bg-[#E4E4E7] transition mb-2"
                data-testid="switch-to-quote-btn"
              >
                Large or Unique Job? Request a Quote Instead for Price Estimates
              </button>
            </div>

            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight uppercase mb-4"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Select Service
            </h2>

            <div className="bg-yellow-50 border-2 border-yellow-600 p-4 mb-6">
              <p className="text-sm font-semibold text-yellow-900">
                <strong>Note:</strong> Prices may vary based on property size and job complexity. Larger properties or more demanding tasks will incur additional charges. "Starting at" prices shown are base rates.
              </p>
            </div>

            <div className="space-y-4">
              {services.map((service) => (
                <button
                  key={service.id}
                  data-testid={`service-${service.id}`}
                  onClick={() => handleServiceSelect(service)}
                  className={`w-full p-6 border-2 border-black text-left transition-all duration-150 ${selectedService === service.id
                    ? 'bg-[#CCFF00] text-black'
                    : 'bg-white text-black hover:bg-[#E4E4E7]'
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-black uppercase text-lg mb-1">{service.name}</h3>
                      <p className="text-sm text-[#71717A]">{service.description}</p>
                    </div>
                    <div className="text-right ml-4">
                      {service.note && <p className="text-xs font-semibold uppercase text-[#71717A]">{service.note}</p>}
                      <p className="text-2xl font-black">
                        {service.price === 0 ? 'FREE' : `$${service.price}`}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <button
              data-testid="next-button"
              onClick={handleNext}
              className="mt-8 w-full py-4 bg-[#CCFF00] text-black border-2 border-black font-bold uppercase text-lg hover:bg-black hover:text-[#CCFF00] transition-all duration-150 flex items-center justify-center gap-2"
            >
              Next <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        )}

        {/* Stage 1: Date Selection */}
        {stage === 1 && (
          <div data-testid="stage-1">
            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight uppercase mb-8 text-center w-full"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Select Date
            </h2>
            <div className="border-2 border-black p-4 sm:p-6 flex justify-center">
              <div className="w-fit mx-auto">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const currentDate = new Date(date);
                    currentDate.setHours(0, 0, 0, 0);
                    return currentDate < minimumBookingDate;
                  }}
                  className="w-fit"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-base sm:text-lg font-bold uppercase",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-8 w-8 sm:h-10 sm:w-10 bg-transparent p-0 border-2 border-black hover:bg-[#CCFF00] transition",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-black rounded-md w-8 sm:w-12 font-bold text-xs sm:text-sm uppercase",
                    row: "flex w-full mt-2",
                    cell: "h-8 w-8 sm:h-12 sm:w-12 text-center text-sm p-0 relative",
                    day: "h-8 w-8 sm:h-12 sm:w-12 p-0 font-bold border-2 border-black hover:bg-[#CCFF00] transition text-xs sm:text-base",
                    day_selected: "bg-[#CCFF00] text-black border-black",
                    day_today: "bg-[#E4E4E7]",
                    day_disabled: "text-[#71717A] opacity-50"
                  }}
                />
                <p className="mt-4 text-sm font-semibold uppercase text-[#71717A]">
                  Bookings must be made at least {MIN_BOOKING_DAYS_AHEAD} days in advance.
                </p>
              </div>
            </div>
            <button
              data-testid="next-button"
              onClick={handleNext}
              className="mt-8 w-full py-4 bg-[#CCFF00] text-black border-2 border-black font-bold uppercase text-lg hover:bg-black hover:text-[#CCFF00] transition-all duration-150 flex items-center justify-center gap-2"
            >
              Next <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        )}

        {/* Stage 2: Time Selection */}
        {stage === 2 && (
          <div data-testid="stage-2">
            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight uppercase mb-4 text-center w-full"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Select Time
            </h2>
            <p className="text-lg text-[#71717A] mb-8">Choose a time between 11 AM and 5 PM</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {TIME_SLOTS.map((time) => (
                (() => {
                  const isOccupied = occupiedSlots.includes(time);
                  return (
                    <button
                      key={time}
                      data-testid={`time-slot-${time}`}
                      onClick={() => {
                        if (isOccupied) return;
                        setSelectedTime(time);
                      }}
                      disabled={isOccupied}
                      className={`py-4 px-6 border-2 border-black font-bold uppercase text-sm transition-all duration-150 ${isOccupied
                        ? 'bg-[#E4E4E7] text-[#71717A] cursor-not-allowed'
                        : selectedTime === time
                          ? 'bg-[#CCFF00] text-black'
                          : 'bg-white text-black hover:bg-[#E4E4E7]'
                        }`}
                    >
                      {isOccupied ? 'OCCUPIED' : time}
                    </button>
                  );
                })()
              ))}
            </div>
            <button
              data-testid="next-button"
              onClick={handleNext}
              className="mt-8 w-full py-4 bg-[#CCFF00] text-black border-2 border-black font-bold uppercase text-lg hover:bg-black hover:text-[#CCFF00] transition-all duration-150 flex items-center justify-center gap-2"
            >
              Next <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        )}

        {/* Stage 3: Personal Details */}
        {stage === 3 && (
          <div data-testid="stage-3">
            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight uppercase mb-8 text-center w-full"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Your Details
            </h2>

            {!showMap ? (
              <div className="space-y-6">
                {/* Name Fields - Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold uppercase text-sm mb-2">First Name *</label>
                    <input
                      data-testid="details-firstname-input"
                      type="text"
                      required
                      value={details.firstName}
                      onChange={(e) => setDetails({ ...details, firstName: e.target.value })}
                      className="w-full h-14 px-4 border-2 border-black text-lg focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold uppercase text-sm mb-2">Last Name *</label>
                    <input
                      data-testid="details-lastname-input"
                      type="text"
                      required
                      value={details.lastName}
                      onChange={(e) => setDetails({ ...details, lastName: e.target.value })}
                      className="w-full h-14 px-4 border-2 border-black text-lg focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                    />
                  </div>
                </div>

                {/* Service Address */}
                <div className="border-2 border-black p-4 bg-[#F4F4F5]">
                  <h3 className="font-bold uppercase text-sm mb-4">Service Address</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-center w-full">
                    <div>
                      <label className="block font-semibold uppercase text-xs mb-2">House/Building Number *</label>
                      <input
                        data-testid="details-housenumber-input"
                        type="text"
                        required
                        value={details.houseNumber}
                        onChange={(e) => setDetails({ ...details, houseNumber: e.target.value })}
                        className="w-full h-12 px-4 border-2 border-black text-base focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold uppercase text-xs mb-2">Street Name *</label>
                      <input
                        data-testid="details-streetname-input"
                        type="text"
                        required
                        value={details.streetName}
                        onChange={(e) => setDetails({ ...details, streetName: e.target.value })}
                        className="w-full h-12 px-4 border-2 border-black text-base focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-semibold uppercase text-xs mb-2">Apt/Lot # (Optional)</label>
                      <input
                        data-testid="details-aptnumber-input"
                        type="text"
                        value={details.aptNumber}
                        onChange={(e) => setDetails({ ...details, aptNumber: e.target.value })}
                        className="w-full h-12 px-4 border-2 border-black text-base focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold uppercase text-xs mb-2">City *</label>
                      <input
                        data-testid="details-city-input"
                        type="text"
                        required
                        value={details.city}
                        readOnly
                        className="w-full h-12 px-4 border-2 border-black text-base bg-gray-100 focus:outline-none"
                        title="We service within 45 miles of Bloomington, IL"
                      />
                      <p className="text-xs text-[#71717A] mt-1">45 mile radius from Bloomington, IL</p>
                    </div>
                    <div>
                      <label className="block font-semibold uppercase text-xs mb-2">Zip Code *</label>
                      <input
                        data-testid="details-zipcode-input"
                        type="text"
                        required
                        maxLength="5"
                        value={details.zipCode}
                        onChange={(e) => setDetails({ ...details, zipCode: e.target.value.replace(/\D/g, '') })}
                        className="w-full h-12 px-4 border-2 border-black text-base focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <label className="block font-semibold uppercase text-sm mb-2">Phone *</label>
                  <input
                    data-testid="details-phone-input"
                    type="tel"
                    required
                    value={details.phone}
                    onChange={(e) => setDetails({ ...details, phone: formatPhoneNumber(e.target.value) })}
                    placeholder="(000)-000-0000"
                    maxLength="14"
                    className="w-full h-14 px-4 border-2 border-black text-lg focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                  />
                </div>
                <div>
                  <label className="block font-semibold uppercase text-sm mb-2">Email *</label>
                  <input
                    data-testid="details-email-input"
                    type="email"
                    required
                    value={details.email}
                    onChange={(e) => setDetails({ ...details, email: e.target.value })}
                    className="w-full h-14 px-4 border-2 border-black text-lg focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
                  />
                </div>

                <button
                  data-testid="next-button"
                  onClick={handleNext}
                  className="mt-8 w-full py-4 bg-[#CCFF00] text-black border-2 border-black font-bold uppercase text-lg hover:bg-black hover:text-[#CCFF00] transition-all duration-150 flex items-center justify-center gap-2"
                >
                  Next <ArrowRight size={20} weight="bold" />
                </button>
              </div>
            ) : (
              /* Address Confirmation with Map */
              <div className="space-y-6">
                <div className="border-2 border-black p-6 bg-white">
                  <h3 className="font-bold uppercase mb-4">Address to Service:</h3>
                  <p className="text-lg mb-4">
                    {details.houseNumber} {details.streetName}{details.aptNumber ? ` ${details.aptNumber}` : ''}<br />
                    {details.city}, IL {details.zipCode}
                  </p>

                  <div className="border-2 border-black h-64 bg-gray-200 flex items-center justify-center">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      src={mapEmbedSrc}
                      title="Service Location Map"
                    ></iframe>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-xl font-bold uppercase mb-6">Is This Address Correct?</p>
                  <div className="flex gap-4">
                    <button
                      data-testid="confirm-address-yes"
                      onClick={() => handleAddressConfirmation(true)}
                      className="flex-1 py-4 bg-[#CCFF00] text-black border-2 border-black font-bold uppercase text-lg hover:bg-black hover:text-[#CCFF00] transition"
                    >
                      Yes
                    </button>
                    <button
                      data-testid="confirm-address-no"
                      onClick={() => handleAddressConfirmation(false)}
                      className="flex-1 py-4 bg-white text-black border-2 border-black font-bold uppercase text-lg hover:bg-red-100 transition"
                    >
                      No
                    </button>
                  </div>
                  <p className="text-sm text-[#71717A] mt-2">Press No To Edit Info</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stage 4: Payment Method */}
        {stage === 4 && (
          <div data-testid="stage-4">
            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight uppercase mb-4 text-center w-full"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Payment Method
            </h2>
            <p className="text-lg text-[#71717A] mb-8">Choose how you'd like to pay</p>

            {/* Summary Card */}
            <div className="border-2 border-black bg-[#F4F4F5] p-6 mb-8">
              <h3 className="font-bold uppercase mb-4">Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold">Service:</span> {services.find(s => s.id === selectedService)?.name}</p>
                <p><span className="font-semibold">Date:</span> {selectedDate && format(selectedDate, 'MMMM dd, yyyy')}</p>
                <p><span className="font-semibold">Time:</span> {selectedTime}</p>
                <p><span className="font-semibold">Name:</span> {details.firstName} {details.lastName}</p>
                <p><span className="font-semibold">Address:</span> {details.houseNumber} {details.streetName}{details.aptNumber ? ` ${details.aptNumber}` : ''}, {details.city}, IL {details.zipCode}</p>
                <p className="text-2xl font-black mt-4">Total: {servicePrice === 0 ? 'FREE' : `$${servicePrice.toFixed(2)}`}</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <button
                data-testid="payment-method-stripe"
                onClick={() => setPaymentMethod('stripe')}
                className={`w-full py-6 px-6 border-2 border-black font-bold uppercase text-lg transition-all duration-150 text-left ${paymentMethod === 'stripe'
                  ? 'bg-[#CCFF00] text-black'
                  : 'bg-white text-black hover:bg-[#E4E4E7]'
                  }`}
              >
                Pay with Stripe (Card)
              </button>
              <button
                data-testid="payment-method-paypal"
                onClick={() => setPaymentMethod('paypal')}
                className={`w-full py-6 px-6 border-2 border-black font-bold uppercase text-lg transition-all duration-150 text-left ${paymentMethod === 'paypal'
                  ? 'bg-[#CCFF00] text-black'
                  : 'bg-white text-black hover:bg-[#E4E4E7]'
                  }`}
              >
                Pay with PayPal
              </button>
              <button
                data-testid="payment-method-cash"
                onClick={() => setPaymentMethod('cash')}
                className={`w-full py-6 px-6 border-2 border-black font-bold uppercase text-lg transition-all duration-150 text-left ${paymentMethod === 'cash'
                  ? 'bg-[#CCFF00] text-black'
                  : 'bg-white text-black hover:bg-[#E4E4E7]'
                  }`}
              >
                Pay Cash on Service Day
              </button>
            </div>

            <button
              data-testid="confirm-booking-btn"
              onClick={handlePayment}
              disabled={loading}
              className="w-full py-4 bg-black text-[#CCFF00] border-2 border-black font-bold uppercase text-lg hover:bg-[#CCFF00] hover:text-black transition-all duration-150 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Confirm Booking'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
